import * as Yup from 'yup'
import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { View, StyleSheet, TouchableOpacity, Platform } from 'react-native'
import { Formik } from 'formik'
import { Button, Txt, CheckBox } from '../../../components'
import { useTheme } from '../../../theming/ThemeProvider'
import KeyboardView from '../../../components/KeyboardView'
import { usePlatforms } from '../../../hooks/usePlatforms'
import Input from '../../../components/Input'
import userActions from '../../../redux/user/userActions'
import { userSelector } from '../../../redux/user/userSelector'
import { useIsDemo } from '../../../hooks/useIsDemo'

const validationSchema = Yup.object({
  password: Yup.string()
    .max(20, 'Must be 20 characters or less')
    .required('Password is required'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required')
})

const LoginForm = ({ loading, onLogin, onResetPassword, onSignUp, onOpenHelpModal, onDemoRedirection }) => {
  const { rememberMe } = useSelector(userSelector)
  const dispatch = useDispatch()
  const { isTablet } = usePlatforms()
  const { theme } = useTheme()
  const styles = useStyles(theme, isTablet)
  const isDemo = useIsDemo()

  const onRememberMe = () => {
    dispatch(userActions.setRememberMe(!rememberMe))
  }

  return (
    <>
      <KeyboardView>

        <View style={[
          styles.form,
          isTablet && { flex: 1, justifyContent: 'center', alignItems: 'center' }
        ]}
        >
          {isTablet && (
            <View style={styles.header}>
              <Txt
                text='Welcome!'
                identity='h1'
                style={styles.title}
              />
            </View>
          )}
          <Formik
            initialValues={{ email: '', password: '' }}
            onSubmit={(values, _) => onLogin(values)}
            validationSchema={validationSchema}
          >
            {({ handleSubmit, values, errors, touched, setFieldValue }) => (
              <View style={{ width: '100%' }}>
                <Input
                  name='email'
                  placeholder='Email'
                  letfIcon='User'
                  value={values.email}
                  onChange={setFieldValue}
                  error={touched.email && errors.email}
                  keyboardType='email-address'
                  testID='loginEmailInput'
                  accessibilityLabel='loginEmailInput'
                />
                <Input
                  name='password'
                  placeholder='Password'
                  letfIcon='Security'
                  value={values.password}
                  onChange={setFieldValue}
                  error={touched.password && errors.password}
                  revealPassword
                  testID='loginPasswordInput'
                  accessibilityLabel='loginPasswordInput'
                />
                <View style={styles.rememberMe}>
                  <CheckBox size={20} checked={rememberMe} onPress={onRememberMe} />
                  <Txt
                    text='Remember me'
                    identity='body2Bold'
                  />
                </View>
                <Button
                  title='Login'
                  loading={loading}
                  onPress={handleSubmit}
                  testID='loginButton'
                  accessibilityLabel='loginButton'
                  style={styles.button}
                />
              </View>
            )}

          </Formik>
          <View style={styles.optionsContainer}>
            <TouchableOpacity
              activeOpacity={0.8}
              onPress={onResetPassword}
              style={styles.option}
              testID='loginFormResetPasswordButton'
              accessibilityLabel='loginFormResetPasswordButton'
            >
              <Txt
                text='Forgot password'
                identity='body2Bold'
              />
            </TouchableOpacity>
            <TouchableOpacity
              activeOpacity={0.8}
              onPress={onOpenHelpModal}
              style={styles.option}
            >
              <Txt
                text='Need help?'
                identity='body2Bold'
              />
            </TouchableOpacity>
          </View>
          {Platform.OS === 'web' && (
            <>
              <TouchableOpacity
                activeOpacity={0.8}
                onPress={onSignUp}
                style={styles.signUp}
              >
                <Txt
                  text='Create an Account'
                  identity='body2'
                  color='SLATE_GRAY'
                />
              </TouchableOpacity>
              {!isDemo && (
                <TouchableOpacity
                  activeOpacity={0.8}
                  onPress={onDemoRedirection}
                  style={styles.demoPortalLink}
                >
                  <Txt
                    text='Go to Demo Portal'
                    identity='body2'
                    color='SLATE_GRAY'
                  />
                </TouchableOpacity>
              )}

            </>
          )}
        </View>
      </KeyboardView>
    </>
  )
}

const useStyles = (theme, isTablet) => StyleSheet.create({
  form: {
    width: '100%',
    maxWidth: isTablet ? 350 : 'auto',
    marginHorizontal: 'auto',
    padding: 20,
    height: isTablet ? '100%' : 'auto',
    minHeight: 250,
    justifyContent: Platform.OS === 'ios' ? 'center' : 'flex-start',
    backgroundColor: theme.WHITE
  },
  optionsContainer: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  header: {
    marginTop: 20,
    marginBottom: 30
  },
  title: {
    marginBottom: 15
  },
  option: {
    paddingRight: 8,
    paddingLeft: 8
  },
  signUp: {
    alignItems: 'center',
    marginTop: 20,
    marginBottom: 5
  },
  rememberMe: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginLeft: -5,
    marginTop: Platform.OS === 'web' ? -15 : -5,
    marginBottom: Platform.OS === 'web' ? 5 : 15
  },
  needHelp: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingRight: 8,
    marginBottom: 10
  },
  button: {
    width: '100%'
  },
  demoPortalLink: {
    alignItems: 'center',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: 40
  }
})

LoginForm.propTypes = {
  loading: PropTypes.bool,
  onLogin: PropTypes.func.isRequired,
  onResetPassword: PropTypes.func.isRequired,
  onSignUp: PropTypes.func.isRequired,
  onOpenHelpModal: PropTypes.func.isRequired,
  onDemoRedirection: PropTypes.func.isRequired
}

LoginForm.defaultProps = {
  loading: false,
  onLogin: () => {},
  onResetPassword: () => {},
  onSignUp: () => {},
  onOpenHelpModal: () => {},
  onDemoRedirection: () => {}
}

export default LoginForm
